import { snowflakeAt } from './time';

/*
 * Kestrel Energy Systems: the one fictional battery maker every product
 * vignette shows (the same company demo/ontologies.js seeds). This module is
 * its structure: the brand, the fixed ids, and the "EVP-750 Pack" ontology.
 * Everything that carries a time is built from a clock in kestrelPassport.js
 * and kestrelWorkflows.js; kestrel.js puts the whole dataset together.
 *
 * Names, units and types follow demo/ontologies.js and demo/lib/domain.js.
 * No real person, customer or supplier appears anywhere: the supplier is as
 * fictional as the manufacturer, and the people are teams.
 */

export const KESTREL = {
    name: 'Kestrel Energy Systems',
    short: 'KES',
    model: 'EVP-750',
    supplier: 'Norrfors Cathode Materials',
};

/** The team a vignette's user belongs to, and the supplier's, as the app names them. */
export const TEAMS = {
    kestrel: { id: 71204, name: 'Kestrel Energy Systems' },
    supplier: { id: 71388, name: 'Norrfors Cathode Materials' },
};

/** Who did what, as the app's `{ id, name }` actors. Teams, never people. */
export const ACTORS = {
    quality: { id: 90411, name: 'Kestrel Quality' },
    supplierDesk: { id: 90527, name: 'Norrfors Supply Desk' },
};

const ONTOLOGY_CREATED = '2026-06-02T08:14:05Z';

/** Property ids, by the name the fixture code uses for them. */
export const P = {};

// Every level's properties in ontology order. A `category` row opens a group,
// the way the ontology editor stores it. `unit` becomes value.measurement;
// `internal` properties are not public (the public endpoint strips them).
const LEVEL_PROPERTIES = {
    A: [
        ['modelName', 'Model name', 'short_text', { required: true }],
        ['batteryCategory', 'Battery category', 'short_text'],
        ['cellCount', 'Cells per pack', 'number'],
        ['warranty', 'Warranty', 'short_text'],
    ],
    B: [
        ['batchCode', 'Batch code', 'short_text', { required: true }],
        ['productionLine', 'Production line', 'short_text'],
        ['producedOn', 'Production started', 'date'],
        ['plannedQuantity', 'Planned quantity', 'number', { internal: true }],
        ['firstPassYield', 'First-pass yield', 'measurement', { unit: '%', internal: true }],
    ],
    C: [
        ['catIdentity', 'Identity', 'category'],
        ['serial', 'Serial number', 'short_text', { required: true }],
        ['manufacturer', 'Manufacturer', 'short_text'],
        ['plant', 'Manufacturing plant', 'short_text'],
        ['manufactured', 'Date of manufacture', 'date'],
        ['catDesign', 'Chemistry and design', 'category'],
        ['chemistry', 'Cell chemistry', 'short_text'],
        ['cellFormat', 'Cell format', 'short_text'],
        ['catPerformance', 'Performance', 'category'],
        ['ratedCapacity', 'Rated capacity', 'measurement', { unit: 'kWh' }],
        ['nominalVoltage', 'Nominal voltage', 'measurement', { unit: 'V' }],
        ['cycleLife', 'Rated cycle life', 'measurement', { unit: 'cycles' }],
        ['catSustainability', 'Sustainability', 'category'],
        ['carbonFootprint', 'Carbon footprint', 'measurement', { unit: 'kg CO₂e/kWh' }],
        ['recycledCobalt', 'Recycled cobalt share', 'measurement', { unit: '%' }],
        ['recycledLithium', 'Recycled lithium share', 'measurement', { unit: '%' }],
        ['recycledNickel', 'Recycled nickel share', 'measurement', { unit: '%' }],
        ['catCondition', 'Condition', 'category'],
        ['stateOfHealth', 'State of health', 'measurement', { unit: '%', internal: true }],
    ],
    D: [
        ['cellSerial', 'Cell serial', 'short_text', { required: true }],
        ['modulePosition', 'Module position', 'short_text'],
        ['cellVoltage', 'Cell voltage', 'measurement', { unit: 'V' }],
        ['cellCapacity', 'Cell capacity', 'measurement', { unit: 'Ah' }],
        ['cellImpedance', 'Cell impedance', 'measurement', { unit: 'mΩ', required: true }],
    ],
};

// The model's mass breakdown, public, for the pack's public page chart. Added
// after every other level so the ids above keep their sequence numbers.
const MODEL_COMPOSITION = [
    ['massCells', 'Mass share, cells', 'measurement', { unit: '%' }],
    ['massHousing', 'Mass share, housing', 'measurement', { unit: '%' }],
    ['massThermal', 'Mass share, thermal system', 'measurement', { unit: '%' }],
    ['massElectronics', 'Mass share, electronics', 'measurement', { unit: '%' }],
];

/**
 * A property as the ontology keeps one, its id minted with the ontology
 * (ONTOLOGY_CREATED) at `sequence`: `unit` becomes value.measurement, and an
 * `internal` property is not public. kestrelModel.js builds the pack's
 * component levels' properties with it too.
 */
export function ontologyProperty(sequence, name, type, { unit, required = false, internal = false } = {}) {
    const value = {};
    if (required) value.required = true;
    if (unit) value.measurement = unit;

    return {
        id: snowflakeAt('property', ONTOLOGY_CREATED, sequence),
        name,
        type,
        value,
        visibility: internal ? { internal: true } : { public: true, internal: true },
    };
}

let sequence = 1;
const define = ([key, name, type, options = {}]) => {
    if (type === 'category') {
        const id = snowflakeAt('property', ONTOLOGY_CREATED, sequence++);
        P[key] = id;

        return { id, type, value: name };
    }

    const property = ontologyProperty(sequence++, name, type, options);
    P[key] = property.id;

    return property;
};
const PROPERTIES = Object.fromEntries(Object.entries(LEVEL_PROPERTIES).map(([depth, rows]) => [depth, rows.map(define)]));
PROPERTIES.A.push(...MODEL_COMPOSITION.map(define));

// The pack unit's safety instructions, a short text written in English and
// German (the hero fills them in), under a Safety group of its own. Added
// after every other property so the ids above keep their sequence numbers.
const PACK_SAFETY = [
    ['catSafety', 'Safety', 'category'],
    ['safetyInstructions', 'Safety instructions', 'short_text'],
];
PROPERTIES.C.push(...PACK_SAFETY.map(define));

/** The fixed ids of every record the vignettes name. */
export const IDS = {
    ontology: snowflakeAt('ontology', ONTOLOGY_CREATED, 0),
    model: snowflakeAt('pass', '2026-06-10T09:02:11Z', 0),
    batch: snowflakeAt('pass', '2026-09-01T06:20:40Z', 0),
    pack: snowflakeAt('pass', '2026-09-08T10:12:30Z', 147),
    import: snowflakeAt('import', '2026-09-08T10:11:58Z', 0),
    supplier: snowflakeAt('sup', '2026-07-15T12:30:00Z', 0),
    dataRequest: snowflakeAt('dreq', '2026-09-24T13:05:00Z', 0),
    // The product photo on the public page's masthead (a Drive file).
    packImage: snowflakeAt('file', '2026-06-10T09:40:00Z', 0),
};

/** The level keys, named. The pack passport lives at `pack`, its cells at `cell`. */
export const LEVELS = { model: 'A', batch: 'B', pack: 'C', cell: 'D' };

/**
 * Each level with `static_lineage` as the server derives it
 * (Ontology::computeStaticAncestry, the level itself not counted): true only
 * when every level above it is singular, so the Batch's is, and the Pack
 * unit's and the Cell's, under a series, are not.
 */
function withLineage(data) {
    const isStatic = (id) => {
        for (let above = data[id].parent; above; above = data[above].parent) {
            if (data[above].type !== 'singular') return false;
        }

        return true;
    };

    return Object.fromEntries(Object.entries(data).map(([id, level]) => [id, { ...level, static_lineage: isStatic(id) }]));
}

const node = (id, name, type, { parent = null, children = [], granularity = null, discoverable = [] } = {}) => ({
    id,
    name,
    type,
    is_root: parent === null,
    ...(parent ? { parent } : {}),
    children,
    ...(granularity ? { granularity } : {}),
    discoverable,
    properties: PROPERTIES[id],
});

/**
 * The EVP-750 ontology as the passport screens read it (`ontologyData` of
 * PassportEdit, `ontology` of the public payload): Model (reported at model
 * granularity) > Batch (batch) > Pack unit (item) > Cell (not reported on its
 * own). Levels follow the API's typing rules: a singular root, the Batch and
 * the Pack unit series under it (the pack a series since 2026-10-05), the
 * cells a collection below.
 */
export const ONTOLOGY = {
    ontology_id: IDS.ontology,
    name: 'EVP-750 Pack',
    description: 'EV traction pack, 96s8p, cylindrical 4680 cells.',
    version: 3,
    latest_version: 3,
    default_language: 'en',
    data: withLineage({
        A: node('A', 'Model', 'singular', { children: ['B'], granularity: 'model' }),
        B: node('B', 'Batch', 'series', { parent: 'A', children: ['C'], granularity: 'batch', discoverable: ['A'] }),
        C: node('C', 'Pack unit', 'series', { parent: 'B', children: ['D'], granularity: 'item', discoverable: ['A', 'B'] }),
        D: node('D', 'Cell', 'many', { parent: 'C', discoverable: ['A', 'B', 'C'] }),
    }),
};

/** A property's definition by id, from whichever level holds it. */
export function propertyById(id) {
    for (const level of Object.values(ONTOLOGY.data)) {
        const found = level.properties.find((property) => property.id === id);
        if (found) return found;
    }

    return null;
}

/**
 * The ontology as the passport list (`sku.list`) returns it, for the
 * OntologyTree row and card: React Flow nodes with their parent/children and
 * the level data under `data`.
 */
export const SKU = {
    ontology_id: IDS.ontology,
    name: ONTOLOGY.name,
    description: ONTOLOGY.description,
    latest_version: ONTOLOGY.latest_version,
    has_draft: false,
    nodes: Object.values(ONTOLOGY.data).map(({ id, parent, children, ...data }) => ({
        id,
        parent: parent ?? null,
        children,
        data,
    })),
};
